(() => {
  if (window.GuruAtlas?.initialize) {
    window.GuruAtlas.initialize(document);
    return;
  }

  const controllers = new WeakMap();
  const lifecycleController = new AbortController();
  const lifecycleSignal = lifecycleController.signal;

  const createIndexController = (root) => {
    const abortController = new AbortController();
    const { signal } = abortController;
    const topicButtons = [...root.querySelectorAll("[data-guru-atlas-topic]")];
    const cards = [...root.querySelectorAll("[data-guru-atlas-card]")];
    const grid = root.querySelector("[data-guru-atlas-grid]");
    const gridInitiallyEmpty = grid?.dataset.guruAtlasInitiallyEmpty === "true";
    const cover = root.querySelector("[data-guru-atlas-cover]");
    const origins = cards.map((card) => ({ card, parent: card.parentElement }));
    const feedCards = origins.filter(({ parent }) => parent === grid).map(({ card }) => card);
    const announcement = root.querySelector("[data-guru-atlas-announcement]");
    const empty = root.querySelector("[data-guru-atlas-empty]");
    const serverPageLinks = [...root.querySelectorAll("[data-guru-atlas-server-page-link]")];
    const reset = root.querySelector("[data-guru-atlas-reset]");
    const serverCurrentPage = Math.max(1, Number.parseInt(root.dataset.guruAtlasServerCurrentPage || "1", 10));
    const serverPageCount = Math.max(1, Number.parseInt(root.dataset.guruAtlasServerPageCount || "1", 10));
    let activeTopic = "all";
    let resultMode = false;

    const setResultMode = (active) => {
      if (!grid || active === resultMode) return;
      resultMode = active;
      root.classList.toggle("is-filtering", active);
      if (active) {
        cards.forEach((card) => grid.append(card));
        grid.hidden = false;
        if (cover) cover.hidden = true;
      } else {
        origins.forEach(({ card, parent }) => parent?.append(card));
        grid.hidden = gridInitiallyEmpty;
        if (cover) cover.hidden = false;
      }
    };

    const writeUrl = (mode = "push") => {
      const url = new URL(window.location.href);
      url.searchParams.delete("topic");
      if (activeTopic !== "all") url.searchParams.set("topic", activeTopic);
      const method = mode === "replace" ? "replaceState" : "pushState";
      window.history[method]({ guruAtlas: true }, "", `${url.pathname}${url.search}${url.hash}`);
    };

    const decorateServerPageLinks = () => {
      const urlBase = window.location.origin === "null" ? "https://guru-atlas.invalid" : window.location.origin;
      serverPageLinks.forEach((link) => {
        const pageUrl = new URL(link.dataset.guruAtlasServerPageUrl || link.href, urlBase);
        pageUrl.searchParams.delete("topic");
        if (activeTopic !== "all") pageUrl.searchParams.set("topic", activeTopic);
        link.setAttribute("href", `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`);
      });
    };

    const apply = ({ announce = true, historyMode = "push" } = {}) => {
      const filtered = activeTopic !== "all";
      setResultMode(filtered);
      const candidateCards = filtered ? cards : feedCards;
      const seenArticleIds = new Set();
      const matches = candidateCards.filter((card) => {
        if (activeTopic !== "all" && card.dataset.topic !== activeTopic) return false;
        const articleId = card.dataset.articleId;
        if (!articleId) return true;
        if (seenArticleIds.has(articleId)) return false;
        seenArticleIds.add(articleId);
        return true;
      });
      const visible = new Set(matches);

      cards.forEach((card) => {
        if (!filtered && !feedCards.includes(card)) {
          card.hidden = false;
          return;
        }
        card.hidden = !visible.has(card);
      });

      if (empty) empty.hidden = matches.length > 0 || (!filtered && cards.length > 0);

      const pageLabel = root.dataset.guruAtlasPageLabel || "Side";
      if (announcement && announce) {
        const resultLabel = matches.length === 1
          ? (root.dataset.guruAtlasResultSingular || "indholdselement")
          : (root.dataset.guruAtlasResultPlural || "indholdselementer");
        announcement.textContent = `${matches.length} ${resultLabel}. ${pageLabel} ${serverCurrentPage} / ${serverPageCount}.`;
      }
      if (historyMode) writeUrl(historyMode);
      decorateServerPageLinks();
      window.GuruMediaPlayback?.syncWithin(root);
    };

    const setTopic = (requestedTopic, { focus = false, announce = true, historyMode = "push" } = {}) => {
      activeTopic = topicButtons.some((button) => button.dataset.guruAtlasTopic === requestedTopic) ? requestedTopic : "all";
      topicButtons.forEach((button) => {
        const active = button.dataset.guruAtlasTopic === activeTopic;
        button.setAttribute("aria-pressed", String(active));
        if (active && focus) {
          button.focus({ preventScroll: true });
          button.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
      });
      apply({ announce, historyMode });
    };

    topicButtons.forEach((button, index) => {
      button.addEventListener("click", () => {
        setTopic(button.dataset.guruAtlasTopic);
      }, { signal });

      button.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        let target = index;
        if (event.key === "ArrowRight") target = (index + 1) % topicButtons.length;
        if (event.key === "ArrowLeft") target = (index - 1 + topicButtons.length) % topicButtons.length;
        if (event.key === "Home") target = 0;
        if (event.key === "End") target = topicButtons.length - 1;
        setTopic(topicButtons[target].dataset.guruAtlasTopic, { focus: true });
      }, { signal });
    });
    reset?.addEventListener("click", () => {
      setTopic("all", { focus: true });
    }, { signal });

    const hydrateFromUrl = ({ announce = false } = {}) => {
      const parameters = new URLSearchParams(window.location.search);
      const rawTopic = parameters.get("topic");
      const requestedTopic = rawTopic || "all";
      setTopic(requestedTopic, { announce, historyMode: null });
      if (requestedTopic !== activeTopic || rawTopic === "all" || rawTopic === "") writeUrl("replace");
    };
    window.addEventListener("popstate", () => hydrateFromUrl({ announce: true }), { signal });
    hydrateFromUrl();
    root.dataset.guruAtlasReady = "true";

    return {
      destroy() {
        setResultMode(false);
        cards.forEach((card) => { card.hidden = false; });
        if (empty) empty.hidden = true;
        delete root.dataset.guruAtlasReady;
        abortController.abort();
      }
    };
  };

  const createArticleController = (root) => {
    const abortController = new AbortController();
    const { signal } = abortController;
    const status = root.querySelector("[data-guru-atlas-share-status]");
    const share = root.querySelector("[data-guru-atlas-share]");
    const timers = new Set();

    const announce = (message) => {
      if (!status || signal.aborted) return;
      status.textContent = message;
      const timer = window.setTimeout(() => {
        if (status.textContent === message) status.textContent = "";
        timers.delete(timer);
      }, 3000);
      timers.add(timer);
    };

    if (share) share.hidden = false;
    share?.addEventListener("click", async () => {
      if (navigator.share) {
        try {
          await navigator.share({
            title: document.title,
            text: root.querySelector(".guru-atlas-story-deck")?.textContent.trim() || "",
            url: window.location.href
          });
          if (signal.aborted) return;
          announce(root.dataset.guruAtlasShareLabel || "Artiklen er delt.");
          return;
        } catch (error) {
          if (error?.name === "AbortError") return;
        }
      }

      try {
        await navigator.clipboard.writeText(window.location.href);
        if (signal.aborted) return;
        announce(root.dataset.guruAtlasCopyLabel || "Linket er kopieret.");
      } catch (_) {
        if (signal.aborted) return;
        announce(root.dataset.guruAtlasCopyFallback || "Kopiér adressen fra browserens adressefelt.");
      }
    }, { signal });

    root.dataset.guruAtlasReady = "true";

    return {
      destroy() {
        delete root.dataset.guruAtlasReady;
        abortController.abort();
        timers.forEach((timer) => window.clearTimeout(timer));
        if (share) share.hidden = true;
        root.querySelectorAll("video").forEach((video) => video.pause());
      }
    };
  };

  const initialize = (scope = document) => {
    const roots = [
      ...(scope.matches?.("[data-guru-atlas-index], [data-guru-atlas-article]") ? [scope] : []),
      ...(scope.querySelectorAll?.("[data-guru-atlas-index], [data-guru-atlas-article]") || [])
    ];
    roots.forEach((root) => {
      if (controllers.has(root)) return;
      const controller = root.hasAttribute("data-guru-atlas-index")
        ? createIndexController(root)
        : createArticleController(root);
      controllers.set(root, controller);
    });
  };

  const destroyWithin = (scope) => {
    const roots = [
      ...(scope.matches?.("[data-guru-atlas-index], [data-guru-atlas-article]") ? [scope] : []),
      ...(scope.querySelectorAll?.("[data-guru-atlas-index], [data-guru-atlas-article]") || [])
    ];
    roots.forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  };

  window.GuruAtlas = {
    initialize,
    destroyWithin,
    destroy() {
      destroyWithin(document);
      lifecycleController.abort();
      delete window.GuruAtlas;
    }
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialize(), { once: true, signal: lifecycleSignal });
  else initialize();

  document.addEventListener("shopify:section:load", (event) => initialize(event.target), { signal: lifecycleSignal });
  document.addEventListener("shopify:section:unload", (event) => destroyWithin(event.target), { signal: lifecycleSignal });
})();
